/**
 * Veículos e associados do SGR no ERP, sob demanda pela placa (spec 032).
 *
 * O ERP NÃO espelha o SGR (D1): um veículo e o associado dele entram quando
 * alguém os ESCOLHE na busca ou ABRE a página deles a partir de um ticket. O
 * SGR é só leitura para o ERP, sempre.
 *
 * Aqui mora o que a API e o navegador precisam concordar:
 * - a classificação do texto digitado (placa, nome ou CPF — §3.4) e o
 *   estreitamento local do resultado (§3.1), que tem de dizer o MESMO que o
 *   SGR diria, senão a lista na tela muda conforme a velocidade de quem digita;
 * - os contratos das rotas (busca, escolha, páginas, pontos de venda);
 * - a procedência da ficha (`sheet_from_sgr`, §2.5).
 */
import { z } from 'zod';
import { Dateish } from './common';
import { normalizeForCompare } from './sgr';
import { TICKET_SHEET_MAX, TicketSheetSchema, type TicketSheetSlots } from './ticket-sheet';
import { SGR_ROUTINE_PHOTO_MARK } from './sgr-routine';

const RefSchema = z.object({ id: z.number().int(), name: z.string() });

// ── Limites e textos fixos ──────────────────────────────────────────────────

/** Os tamanhos do associado são os da ficha (spec 031): o valor de um vai para o outro. */
export const END_CUSTOMER_MAX = {
  name: TICKET_SHEET_MAX.name,
  phone: TICKET_SHEET_MAX.phone,
  phoneAlt: TICKET_SHEET_MAX.phoneAlt,
  street: TICKET_SHEET_MAX.street,
  number: TICKET_SHEET_MAX.number,
  complement: TICKET_SHEET_MAX.complement,
  neighborhood: TICKET_SHEET_MAX.neighborhood,
  city: TICKET_SHEET_MAX.city,
  sgrStatus: 60,
} as const;

/** Os campos do veículo no recurso: os tamanhos da ficha também. */
export const VEHICLE_FIELD_MAX = {
  brand: TICKET_SHEET_MAX.brand,
  model: TICKET_SHEET_MAX.model,
  color: TICKET_SHEET_MAX.color,
  year: TICKET_SHEET_MAX.year,
  chassis: TICKET_SHEET_MAX.chassis,
  sgrStatus: 60,
} as const;

/**
 * A chave do tipo de recurso de sistema — o ERP acha o tipo por ela, nunca pelo
 * nome. O tipo NASCE com o termo de recurso do perfil do tenant ("Veículo" no
 * preset de rastreamento: palavra de vertical é dado do perfil, spec 017), e a
 * pessoa pode renomear.
 */
export const SGR_VEHICLE_TYPE_KEY = 'sgr_vehicle';

/** como a sugestão de um ponto de venda foi achada (§8) */
export const SGR_SUGGESTION_KINDS = ['alias', 'exact', 'approximate'] as const;
export type SgrSuggestionKind = (typeof SGR_SUGGESTION_KINDS)[number];

// ── A busca (§3) ────────────────────────────────────────────────────────────

/** letras ou dígitos depois de normalizar — menos que isto, nenhuma consulta */
export const SGR_SEARCH_MIN_CHARS = 3;
/**
 * A pausa depois da última tecla. A consulta custa ~1,8 s com 3 ou com 7
 * caracteres; uma pausa curta faria uma consulta por letra de quem digita
 * devagar, sem ganho nenhum (§3.1).
 */
export const SGR_SEARCH_DEBOUNCE_MS = 400;
/** "Buscando no SGR…" só aparece depois disto */
export const SGR_SEARCH_SPINNER_DELAY_MS = 300;
/** linhas pedidas ao SGR por busca (`length`) */
export const SGR_SEARCH_PAGE = 50;
/** linhas mostradas na tela */
export const SGR_SEARCH_VISIBLE = 10;

/**
 * Situações que vão por último e com aviso (resposta 3 do operador): aparecem,
 * podem ser escolhidas, e nada bloqueia em mesa nenhuma.
 */
export const SGR_INACTIVE_STATUSES: readonly string[] = ['INATIVO', 'CANCELADO'];

export function sgrStatusInactive(status: string | null | undefined): boolean {
  return status != null && SGR_INACTIVE_STATUSES.includes(normalizeForCompare(status));
}

export type SgrQueryKind = 'plate' | 'name' | 'cpf';

export interface SgrSearchQuery {
  kind: SgrQueryKind;
  /** o texto que vai ao SGR (ou que estreita na tela) */
  q: string;
}

/** maiúscula, só letras e dígitos (acentuadas ficam: o nome do associado tem) */
const alnum = (text: string) => text.toUpperCase().replace(/[^\p{L}\p{N}]/gu, '');

/** CPF por dígito verificador — 11 dígitos repetidos não são CPF */
export function isValidCpf(raw: string): boolean {
  const d = raw.replace(/\D/g, '');
  if (d.length !== 11 || /^(\d)\1{10}$/.test(d)) return false;
  const digit = (len: number) => {
    let sum = 0;
    for (let i = 0; i < len; i += 1) sum += Number(d[i]) * (len + 1 - i);
    const rest = (sum * 10) % 11;
    return rest === 10 ? 0 : rest;
  };
  return digit(9) === Number(d[9]) && digit(10) === Number(d[10]);
}

/**
 * O texto digitado → o que vai ao SGR (§3.1, §3.4). O TIPO decide:
 * - 11 dígitos de um CPF VÁLIDO (com ou sem pontuação) → busca de CLIENTE;
 * - duas ou mais palavras só de letras ("MARIA SILVA") → busca de veículo pelo
 *   NOME, com os espaços (sem eles o SGR não acharia o nome);
 * - todo o resto, inclusive só dígitos (o final da placa) → busca de veículo
 *   pela PLACA, sem hífen e sem espaço (com hífen o SGR acha 0).
 *
 * NUNCA dígitos soltos na busca de cliente: a do SGR é por substring (6
 * dígitos acharam um cliente), e repassar dígitos viraria um enumerador de CPF.
 * Menos de 3 letras ou dígitos → null (nenhuma consulta).
 */
export function classifySgrSearch(raw: string): SgrSearchQuery | null {
  const text = raw.normalize('NFC').trim();
  if (alnum(text).length < SGR_SEARCH_MIN_CHARS) return null;
  if (/^[\d.\-\s/]+$/.test(text)) {
    const digits = text.replace(/\D/g, '');
    if (digits.length === 11 && isValidCpf(digits)) return { kind: 'cpf', q: digits };
  }
  const words = text.split(/\s+/).filter(Boolean);
  if (words.length >= 2 && words.every((w) => /^\p{L}+$/u.test(w))) {
    return { kind: 'name', q: words.join(' ').toUpperCase() };
  }
  return { kind: 'plate', q: alnum(text) };
}

/** "123.456.789-09" → "***.456.789-**" (montado no servidor; a tela nunca vê o CPF inteiro) */
export function maskCpf(raw: string | null | undefined): string | null {
  const d = (raw ?? '').replace(/\D/g, '');
  if (d.length !== 11) return null;
  return `***.${d.slice(3, 6)}.${d.slice(6, 9)}-**`;
}

/**
 * As duas grafias da mesma placa (§7): a antiga `ABC1234` e a Mercosul
 * `ABC1C34` — o 5º caractere troca dígito ↔ letra (0→A … 9→J). Placa que não
 * tem nenhuma das duas formas volta sozinha.
 */
export function plateVariants(plate: string | null | undefined): string[] {
  const key = (plate ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (key === '') return [];
  if (/^[A-Z]{3}[0-9]{4}$/.test(key)) {
    return [key, `${key.slice(0, 4)}${String.fromCharCode(65 + Number(key[4]))}${key.slice(5)}`];
  }
  if (/^[A-Z]{3}[0-9][A-J][0-9]{2}$/.test(key)) {
    return [key, `${key.slice(0, 4)}${key.charCodeAt(4) - 65}${key.slice(5)}`];
  }
  return [key];
}

/** onde a busca casou a linha — decide a ordem (§3.1) */
export type SgrMatch = 'plate_start' | 'plate' | 'other';

export function sgrMatchOf(plate: string, query: SgrSearchQuery): SgrMatch {
  if (query.kind !== 'plate') return 'other';
  const p = alnum(plate);
  if (p.startsWith(query.q)) return 'plate_start';
  if (p.includes(query.q)) return 'plate';
  return 'other';
}

/** um item da busca de veículo, como a tela o recebe */
export const SgrVehicleItemSchema = z.object({
  /** `cod_veiculo` — o id numérico do link da linha */
  sgrId: z.string(),
  plate: z.string(),
  model: z.string().nullable(),
  ownerName: z.string().nullable(),
  status: z.string().nullable(),
  /** INATIVO ou CANCELADO: vai por último, em cinza, com aviso */
  inactive: z.boolean(),
  match: z.enum(['plate_start', 'plate', 'other']),
  /** o recurso do ERP quando o veículo já foi escolhido ("Já no ERP") */
  equipmentId: z.number().int().nullable(),
});
export type SgrVehicleItem = z.infer<typeof SgrVehicleItemSchema>;

const MATCH_ORDER: Record<SgrMatch, number> = { plate_start: 0, plate: 1, other: 2 };

/**
 * A ordem da lista (§3.1 + resposta 3): placa que COMEÇA com o texto, placa que
 * CONTÉM, e o que casou por nome ou modelo; INATIVO e CANCELADO no FIM da lista
 * inteira, na mesma ordem entre si.
 *
 * O empate sai pela PLACA (e pelo id do SGR), nunca pela posição na página do
 * SGR: o estreitamento local (sgrQueryNarrows) refaz a ordem a partir da lista
 * JÁ ordenada do texto anterior, e uma linha que muda de classe ("RTX" casa a
 * placa, "RTX4" só o modelo) cairia em outra posição do que a consulta nova
 * daria. Com uma ordem total, a lista estreitada é a MESMA que o SGR daria
 * (provado em apps/api/src/shared/sgr-search-controller.test.ts).
 */
export function sortSgrVehicles<T extends Pick<SgrVehicleItem, 'match' | 'inactive' | 'plate' | 'sgrId'>>(items: readonly T[]): T[] {
  return [...items].sort(
    (a, b) =>
      Number(a.inactive) - Number(b.inactive) ||
      MATCH_ORDER[a.match] - MATCH_ORDER[b.match] ||
      a.plate.localeCompare(b.plate, 'en') ||
      a.sgrId.localeCompare(b.sgrId, 'en', { numeric: true })
  );
}

/**
 * O estreitamento local (§3.1): o SGR busca por SUBSTRING em placa, modelo,
 * nome do associado e situação. O resultado de "QXD1" está dentro do de "QXD",
 * e por isso, com o de "QXD" COMPLETO na memória, a tela filtra sem consultar —
 * pela MESMA regra de substring, senão a lista mudaria conforme a velocidade de
 * quem digita.
 */
export function sgrItemMatches(item: Pick<SgrVehicleItem, 'plate' | 'model' | 'ownerName' | 'status'>, query: SgrSearchQuery): boolean {
  const fields = [item.plate, item.model, item.ownerName, item.status].filter(
    (f): f is string => typeof f === 'string' && f !== ''
  );
  if (query.kind === 'name') {
    const needle = normalizeForCompare(query.q);
    return fields.some((f) => normalizeForCompare(f).includes(needle));
  }
  const needle = alnum(query.q);
  return fields.some((f) => alnum(f).includes(needle));
}

/**
 * O resultado de `prev` serve para `next` sem consultar? Só quando é o MESMO
 * tipo de busca e o texto novo CONTÉM o anterior (substring de substring) — e
 * o anterior veio completo (quem chama confere).
 */
export function sgrQueryNarrows(prev: SgrSearchQuery, next: SgrSearchQuery): boolean {
  return prev.kind === next.kind && prev.kind !== 'cpf' && next.q.includes(prev.q);
}

/** um item da busca por CPF: o nome, o CPF mascarado e a situação — nunca o CPF inteiro */
export const SgrCustomerItemSchema = z.object({
  name: z.string(),
  cpfMasked: z.string(),
  status: z.string().nullable(),
});
export type SgrCustomerItem = z.infer<typeof SgrCustomerItemSchema>;

/**
 * Por que o SGR não respondeu (§10). Nenhum vira "lista vazia": vazio só sai
 * de uma resposta válida do SGR com `recordsFiltered = 0`.
 */
export const SGR_UNAVAILABLE_REASONS = [
  /** a sessão caiu duas vezes seguidas */
  'session_failed',
  /** usuário ou senha da integração recusados */
  'login_failed',
  /** a API v1 recusou a integração (401 "Acesso a API da HINOVA foi negado") */
  'api_denied',
  'timeout',
  'network_error',
  'http_error',
  /** HTML ou JSON fora do contrato no lugar da resposta */
  'invalid_response',
  /** linhas sem id numérico além de 10% (a regra dos técnicos) */
  'format_changed',
  /** já há 4 chamadas ao SGR em voo neste processo, e a espera passou de 2 s */
  'busy',
  /** mais de 30 buscas por minuto desta pessoa */
  'rate_limited',
  /** uma busca mais nova da mesma pessoa cancelou esta */
  'superseded',
] as const;
export type SgrUnavailableReason = (typeof SGR_UNAVAILABLE_REASONS)[number];

const Unavailable = z.object({ status: z.literal('unavailable'), reason: z.enum(SGR_UNAVAILABLE_REASONS) });

/** POST /sgr/vehicles/search — POST porque a placa e o CPF são dado pessoal, e o logger escreve a URL */
export const SgrSearchBodySchema = z
  .object({
    q: z.string().max(120),
    /**
     * Os veículos de UMA pessoa (escolhida na busca por CPF, ou a página do
     * associado): busca pelo nome, ficando só as linhas em que o nome bate
     * exatamente, e o aviso de homônimo.
     */
    owner: z.boolean().optional(),
  })
  .strict();
export type SgrSearchBody = z.infer<typeof SgrSearchBodySchema>;

export const SgrSearchResponseSchema = z.union([
  z.object({
    status: z.literal('ok'),
    kind: z.literal('vehicles'),
    /** a consulta feita, para a tela guardar com o resultado */
    query: z.object({ kind: z.enum(['plate', 'name', 'cpf']), q: z.string() }),
    items: z.array(SgrVehicleItemSchema),
    /** `recordsFiltered` do SGR */
    total: z.number().int(),
    /** o SGR devolveu TODAS as linhas que casam (`total <= items`): dá para estreitar na tela */
    complete: z.boolean(),
    /** busca por dono: mais de uma pessoa com esse nome no SGR */
    homonyms: z.boolean().optional(),
  }),
  z.object({
    status: z.literal('ok'),
    kind: z.literal('customers'),
    items: z.array(SgrCustomerItemSchema),
    total: z.number().int(),
  }),
  Unavailable,
]);
export type SgrSearchResponse = z.infer<typeof SgrSearchResponseSchema>;

// ── A escolha (§4) ──────────────────────────────────────────────────────────

/** validade do recibo do /pick (como o recibo da rota da cotação, 029) */
export const SGR_RECEIPT_TTL_MS = 12 * 60 * 60 * 1000;

export const SgrPickResponseSchema = z.union([
  z.object({
    status: z.literal('ok'),
    /** o recurso (criado ou reaproveitado pelo `sgr_id`) */
    equipment: z.object({
      id: z.number().int(),
      name: z.string(),
      /** o cliente ligado ao ponto de venda; null = ponto ainda sem cliente */
      clientId: z.number().int().nullable(),
      clientName: z.string().nullable(),
    }),
    endCustomer: RefSchema,
    salePoint: z
      .object({
        sgrId: z.string(),
        name: z.string().nullable(),
        client: RefSchema.nullable(),
      })
      .nullable(),
    /** o ponto de venda não está ligado: a tela pede o cliente do TICKET (nada é adivinhado) */
    needsClient: z.boolean(),
    vehicleStatus: z.string().nullable(),
    inactive: z.boolean(),
    /** os 16 slots da ficha como o SGR os dá (null = o SGR não tem) */
    sheet: TicketSheetSchema,
    /** o recibo assinado que o POST/PATCH do ticket leva em `sheetFromSgr` */
    receipt: z.string(),
  }),
  z.object({ status: z.literal('not_found') }),
  Unavailable,
]);
export type SgrPickResponse = z.infer<typeof SgrPickResponseSchema>;

/** O título sugerido no "Novo ticket": `PLACA-CIDADE-NOME`, o formato do worker. */
export function sgrTicketTitle(sheet: Pick<TicketSheetSlots, 'plate' | 'city' | 'name'>): string | null {
  const parts = [sheet.plate, sheet.city, sheet.name].map((p) => (p ?? '').trim());
  return parts.every((p) => p !== '') ? parts.join('-') : null;
}

// ── A procedência da ficha (§2.5) ───────────────────────────────────────────

/** O que `tickets.sheet_from_sgr` guarda — escrito pelo servidor a partir do recibo, nunca do navegador. */
export const SheetFromSgrStoredSchema = z.object({
  vehicleSgrId: z.string(),
  endCustomerSgrId: z.string(),
  /** quando o veículo foi lido do SGR (ISO) */
  at: z.string(),
  /** quem escolheu; null = a rotina noturna (spec 035, D4), que não é usuário */
  byId: z.number().int().nullable(),
  /**
   * A foto foi tirada pela rotina noturna do Recurso (spec 035, D4): não há
   * pessoa nem usuário de serviço — a rotina é o autor, e o cartão diz "do SGR
   * (07/10, rotina diária)". Ausente = foto de uma pessoa (o recibo do /pick).
   */
  routine: z.literal(SGR_ROUTINE_PHOTO_MARK).optional(),
  slots: TicketSheetSchema,
});
export type SheetFromSgrStored = z.infer<typeof SheetFromSgrStoredSchema>;

/** Como o detalhe do ticket a devolve: com o nome de quem escolheu. */
export const SheetFromSgrViewSchema = z.object({
  vehicleSgrId: z.string(),
  endCustomerSgrId: z.string(),
  at: Dateish,
  by: RefSchema.nullable(),
  /** a foto é da rotina noturna (spec 035, D4); ausente na API anterior */
  routine: z.boolean().optional(),
  slots: TicketSheetSchema,
});
export type SheetFromSgrView = z.infer<typeof SheetFromSgrViewSchema>;

/** POST /tickets/:n/sheet/sgr-refresh — "Atualizar do SGR" no cartão (§6) */
export const SheetSgrRefreshResponseSchema = z.union([
  z.object({
    status: z.literal('ok'),
    /** slots cujo valor efetivo ainda era igual à foto e o SGR mudou: atualizados */
    updated: z.array(z.string()),
    /**
     * slots que ficaram como estavam, diferentes do SGR: o valor efetivo é
     * diferente da foto (editado depois dela, ou a descrição com outro valor), ou
     * o SGR deixou de ter o valor que só a descrição traz (`sgrValue: null`).
     * Com `sgrValue`, o cartão mostra "No SGR: X"
     */
    kept: z.array(z.object({ slot: z.string(), sgrValue: z.string().nullable() })),
  }),
  z.object({ status: z.literal('not_found') }),
  Unavailable,
]);
export type SheetSgrRefreshResponse = z.infer<typeof SheetSgrRefreshResponseSchema>;

// ── Páginas (§7) ────────────────────────────────────────────────────────────

/** passou disto, a página pede `POST …/sgr-refresh` ao abrir (o GET nunca escreve) */
export const SGR_STALE_AFTER_MS = 24 * 60 * 60 * 1000;
/** "Atualizar do SGR" da página: no máximo 1 por minuto por recurso/associado */
export const SGR_REFRESH_MIN_INTERVAL_MS = 60 * 1000;

export const EquipmentSgrSchema = z.object({
  sgrId: z.string(),
  brand: z.string().nullable(),
  model: z.string().nullable(),
  color: z.string().nullable(),
  year: z.string().nullable(),
  chassis: z.string().nullable(),
  status: z.string().nullable(),
  inactive: z.boolean(),
  endCustomer: RefSchema.nullable(),
  salePoint: z.object({ sgrId: z.string(), name: z.string().nullable() }).nullable(),
  syncedAt: Dateish.nullable(),
  /** a leitura funcionou e o SGR não tem mais o veículo — nada é apagado */
  missingSince: Dateish.nullable(),
  /** passou de 24 h desde a última leitura */
  stale: z.boolean(),
});
export type EquipmentSgr = z.infer<typeof EquipmentSgrSchema>;

export const EndCustomerVehicleSchema = z.object({
  /** o recurso do ERP */
  id: z.number().int(),
  plate: z.string(),
  brand: z.string().nullable(),
  model: z.string().nullable(),
  status: z.string().nullable(),
  inactive: z.boolean(),
  client: RefSchema.nullable(),
});

export const EndCustomerDetailSchema = z.object({
  id: z.number().int(),
  sgrId: z.string(),
  name: z.string(),
  phone: z.string().nullable(),
  phoneAlt: z.string().nullable(),
  street: z.string().nullable(),
  number: z.string().nullable(),
  complement: z.string().nullable(),
  neighborhood: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  status: z.string().nullable(),
  syncedAt: Dateish,
  missingSince: Dateish.nullable(),
  stale: z.boolean(),
  /** os veículos dele que estão NO ERP (os outros vêm pela busca, ao vivo) */
  vehicles: z.array(EndCustomerVehicleSchema),
});
export type EndCustomerDetail = z.infer<typeof EndCustomerDetailSchema>;

/** POST /resources/:id/sgr-refresh e /end-customers/:id/sgr-refresh */
export const SgrRefreshResponseSchema = z.union([
  /** `refreshed: false` = a última leitura tem menos de 1 minuto */
  z.object({ status: z.literal('ok'), refreshed: z.boolean() }),
  z.object({ status: z.literal('not_found') }),
  Unavailable,
]);
export type SgrRefreshResponse = z.infer<typeof SgrRefreshResponseSchema>;

// ── Pontos de venda → cliente (§8) ──────────────────────────────────────────

export const SgrSalePointSchema = z.object({
  id: z.number().int(),
  sgrId: z.string(),
  name: z.string().nullable(),
  client: RefSchema.nullable(),
  suggestion: z.object({ client: RefSchema, kind: z.enum(SGR_SUGGESTION_KINDS) }).nullable(),
  /** ligado ao cliente de venda direta configurado */
  directSale: z.boolean(),
  linkedBy: RefSchema.nullable(),
  linkedAt: Dateish.nullable(),
  /** quantos veículos dele já estão no ERP */
  vehiclesInErp: z.number().int(),
});
export type SgrSalePoint = z.infer<typeof SgrSalePointSchema>;

export const SgrSalePointListSchema = z.object({
  items: z.array(SgrSalePointSchema),
  /** a última leitura da lista do SGR; null = nunca lida */
  syncedAt: Dateish.nullable(),
  stale: z.boolean(),
  directSaleClient: RefSchema.nullable(),
});
export type SgrSalePointList = z.infer<typeof SgrSalePointListSchema>;

export const SgrSalePointListQuerySchema = z.object({
  /** só os "pontos de venda sem cliente" */
  pending: z.enum(['true', 'false', '1', '0']).transform((v) => v === 'true' || v === '1').optional(),
});

/** POST /sgr/sale-points/:id/link — `null` desliga (os recursos ficam sem associação) */
export const SgrSalePointLinkBodySchema = z.object({ clientId: z.number().int().positive().nullable() }).strict();
export type SgrSalePointLinkBody = z.infer<typeof SgrSalePointLinkBodySchema>;

export const SgrSalePointLinkPreviewQuerySchema = z.object({
  client_id: z.coerce.number().int().positive().optional(),
});

/** o que ligar o ponto mexe, mostrado ANTES do clique */
export const SgrSalePointLinkPreviewSchema = z.object({
  /** recursos do SGR deste ponto que mudam de cliente */
  resources: z.number().int(),
  /** tickets abertos ligados a esses recursos (eles NÃO mudam de cliente) */
  openTickets: z.number().int(),
});
export type SgrSalePointLinkPreview = z.infer<typeof SgrSalePointLinkPreviewSchema>;

export const SgrSalePointSyncResponseSchema = z.union([
  z.object({ status: z.literal('ok'), count: z.number().int() }),
  Unavailable,
]);

export const SgrSettingsSchema = z.object({ directSaleClient: RefSchema.nullable() });
export type SgrSettings = z.infer<typeof SgrSettingsSchema>;
export const SgrSettingsBodySchema = z.object({ directSaleClientId: z.number().int().positive().nullable() }).strict();

/** GET /sgr/status — o que a tela de Configurações e a busca precisam saber */
export const SgrStatusSchema = z.object({
  /**
   * O nome da origem na tela ("SGR") — vem da API, e não do SPA: o nome do
   * sistema de uma vertical não mora no bundle (guarda de vocabulário, spec 017)
   */
  sourceName: z.string(),
  /** a integração é deste tenant (SGR_TENANT_SLUG); nos outros a busca não aparece */
  available: z.boolean(),
  enabled: z.boolean(),
  /** NOMES das variáveis ausentes ou inválidas — nunca valores */
  missingConfig: z.array(z.string()),
  /** a sessão deste processo (cada réplica tem a sua) */
  session: z.object({
    state: z.enum(['none', 'open', 'failed']),
    reason: z.string().nullable(),
    at: Dateish.nullable(),
  }),
});
export type SgrStatus = z.infer<typeof SgrStatusSchema>;
